import { makeStyles } from '@mui/styles';
import Grid from 'apollo-react/components/Grid';
import Loader from 'apollo-react/components/Loader';
import Typography from 'apollo-react/components/Typography';
import _ from 'lodash';
import qs from 'qs';
import React, { useEffect, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import AdvancedSearchPanel from './AdvancedSearchPanel';
import Header from './Header';
import EnhancedSearchResult from './SearchResult';
import SearchResultLoader from './SearchResultLoader';
import {
  fetchEnhancedSearchResultsPage,
  makeEnhancedSearchAdvanced,
  resetSearchResults,
} from '../../actions/SearchPageActions';
import {
  getEnhancedSearchTreeViewData,
  resetSearchTreeViewData,
} from '../../actions/SearchTreeViewDataActions';
import Layout from '../../components/Layout';
import { PILGRIM_TOP_MENU_HEIGHT, SIDEBAR_WIDTH } from '../../constants/SizeConstants';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { getAppOptionByKey } from '../../selectors/AppConfigsSelectors';
import { parseDateString } from '../../utils/dateUtils';

const useStyles = makeStyles(({ palette }) => ({
  loaderOverlay: {
    marginLeft: SIDEBAR_WIDTH,
    marginTop: PILGRIM_TOP_MENU_HEIGHT,
  },
  results: {
    backgroundColor: 'white',
    padding: '12px 24px 0px 24px',
  },
  resultsHeader: {
    fontWeight: 600,
    color: 'black',
  },
  resultItem: {
    borderBottomColor: palette?.grey[300],
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
  },
}));

const getResults = (rows) => {
  if (!rows) {
    return [];
  }

  const output = rows.map((row) => {
    const results = Object.values(row);

    return results.flat().map((result) => ({
      entityType: result.EntityId,
      formKey: `${result.EntityId}_DETAIL`,
      itmId: result.ObjectId,
      modifiedDate: parseDateString(result.ModifiedDate),
      phase: result.Phase,
      recordNumber: result.RecordNumber,
      status: result.Status,
      title: result.Title,
      translatedEntityName: result.TranslatedEntityName,
      fileName: result.FileName,
      crossReferences: result.ChildCrossEntities.map((entity) => ({
        entityName: entity?.ECR_REF_ENTITY_TYPE_NAME,
        itmId: entity?.ECR_RELATED_OBJ_ID,
      })),
    }));
  });

  return _.flattenDeep(output);
};

const SearchPage = () => {
  const [currentSearchQuery, setCurrentSearchQuery] = useState('');
  const [currentEntityType, setCurrentEntityType] = useState('');

  const [isCriteriaFetched, setIsCriteriaFetched] = useState(false);

  const {
    totalResultCount,
    getCrossReferences,
    loading,
    searching,
    page,
    totalPages,
    isAdvancedSearch,
    searchText,
    advancedSearchCriteria,
    entityNames,
    useContainsTable,
    searchOption,
    rows,
  } = useSelector((state) => state.searchPage, shallowEqual);

  const allowEnhancedGlobalSearch = useSelector(getAppOptionByKey('allowEnhancedGlobalSearch'));

  const classes = useStyles();
  const dispatch = useDispatch();
  const location = useLocation();
  const translateString = useTranslation();

  const results = useMemo(() => getResults(rows), [rows]);

  const hasMoreResults = page + 1 < totalPages;

  const { entityType = '', searchQuery = '' } = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  });

  useEffect(() => {
    if (entityType) {
      const getAdvancedSearchCriteria = async () => {
        await dispatch(getEnhancedSearchTreeViewData(entityType));

        setIsCriteriaFetched(true);
      };

      getAdvancedSearchCriteria();
    }

    return () => {
      setIsCriteriaFetched(false);

      dispatch(resetSearchTreeViewData());
      dispatch(resetSearchResults());
      dispatch(
        makeEnhancedSearchAdvanced({
          isAdvancedSearch: false,
          searchText: '',
          page: 0,
          currentPageIndex: 0,
          searching: false,
          loading: false,
          advancedSearchCriteria: [],
        })
      );
      sessionStorage.removeItem('backButtonURL');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      searchQuery !== '' &&
      !isAdvancedSearch &&
      isCriteriaFetched &&
      searchQuery !== currentSearchQuery &&
      searchQuery !== searchText
    ) {
      dispatch(resetSearchResults());
      setCurrentEntityType(entityType);
      setCurrentSearchQuery(searchQuery);
      document.title = `Search Results: ${searchQuery}`;
      dispatch(
        fetchEnhancedSearchResultsPage({
          searchText: searchQuery,
          entityNames: entityType !== 'ALL' ? [entityType] : [],
          advancedSearchCriteria,
        })
      );
    }
  }, [
    dispatch,
    advancedSearchCriteria,
    entityType,
    searchQuery,
    isAdvancedSearch,
    currentSearchQuery,
    isCriteriaFetched,
    searchText,
  ]);

  useEffect(() => {
    if (entityNames?.length) {
      setCurrentEntityType(entityNames[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityNames]);

  const loadMore = () => {
    if (searchText?.trim() !== '' && !loading && !searching && hasMoreResults) {
      dispatch(makeEnhancedSearchAdvanced({ loading: true, searching: true }));
      dispatch(
        fetchEnhancedSearchResultsPage({
          searchText,
          entityNames,
          advancedSearchCriteria,
          page: page + 1,
          useContainsTable,
          searchOption: searchOption ?? 0,
          getCrossReferences,
        })
      );
    }
  };

  return (
    <Layout>
      <Header
        resultsCount={totalResultCount}
        searchQuery={currentSearchQuery}
        entityType={currentEntityType}
      />
      {allowEnhancedGlobalSearch && (
        <AdvancedSearchPanel entityType={currentEntityType} searchQuery={currentSearchQuery} />
      )}
      {(loading && searching && totalResultCount === 0 && <Loader />) ||
        (totalResultCount > 0 && (
          <InfiniteScroll
            pageStart={0}
            loadMore={loadMore}
            hasMore={hasMoreResults}
            loader={loading && <SearchResultLoader key={0} />}
          >
            <Grid className={classes.results} container>
              <Grid item xs={12}>
                <Typography className={classes.resultsHeader} variant="subtitle1" component="h3">
                  {translateString('Last Edited Date')}
                </Typography>
              </Grid>
              {results.map((result, key) => (
                <Grid key={`${result.itmId}${key}`} className={classes.resultItem} item xs={12}>
                  <EnhancedSearchResult
                    entityType={result.entityType}
                    formKey={result.formKey}
                    itmId={result.itmId}
                    modifiedDate={result.modifiedDate}
                    recordNumber={result.recordNumber}
                    status={result.status}
                    title={result.title}
                    translatedEntityName={result.translatedEntityName}
                    phase={result.phase}
                    fileName={result.fileName}
                    // For now, cross references keep hidden
                    // use showCrossReferences={getCrossReferences} property to show/hide them
                    showCrossReferences={getCrossReferences}
                    crossReferences={result.crossReferences}
                  />
                </Grid>
              ))}
            </Grid>
          </InfiniteScroll>
        ))}
    </Layout>
  );
};

export default SearchPage;
